import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ludus/ui'
import type { AuditSummary } from '../types.ts'
import { formatAmount, formatDate, formatPeriod } from '../utils/format.ts'
import { AuditStateBadge } from './AuditStateBadge.tsx'

interface AuditsListViewProps {
  audits: AuditSummary[]
  onCreate: () => void
}

/** The §10.1 columns: title, pipelines, period, cutoff, state, spend / quota, anomalies, created. */
export function AuditsListView({ audits, onCreate }: AuditsListViewProps) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full max-w-6xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-foreground">
            {t('pages.audits.list.title', { defaultValue: 'Audits' })}
          </h1>
          <p className="text-xs text-muted">
            {t('pages.audits.list.subtitle', {
              defaultValue: 'A frozen slice of the mirror, processed stage by stage.',
            })}
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('pages.audits.list.newAudit', { defaultValue: 'New audit' })}
        </Button>
      </header>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t('pages.audits.list.columns.title', { defaultValue: 'Title' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.pipelines', { defaultValue: 'Pipelines' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.period', { defaultValue: 'Period' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.cutoff', { defaultValue: 'Cutoff' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.state', { defaultValue: 'State' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.spend', { defaultValue: 'Spend / quota' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.anomalies', { defaultValue: 'Anomalies' })}
              </TableHead>
              <TableHead>
                {t('pages.audits.list.columns.created', { defaultValue: 'Created' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audits.map((audit) => (
              <TableRow key={audit.id}>
                <TableCell>
                  <Link
                    className="font-medium text-foreground hover:underline"
                    to={`/audits/${audit.id}`}
                  >
                    {audit.title}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-muted">{audit.pipelineIds.join(', ')}</TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {formatPeriod(audit.periodFrom, audit.periodTo)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {formatDate(audit.cutoffAt)}
                </TableCell>
                <TableCell>
                  <AuditStateBadge state={audit.state} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {audit.spend
                    ? `${formatAmount(audit.spend.spent.amount, audit.quota.currency)} / ${formatAmount(audit.quota.amount, audit.quota.currency)}`
                    : '—'}
                </TableCell>
                <TableCell className="text-xs">
                  {audit.anomalies === null ? '—' : audit.anomalies}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs text-muted">
                  {formatDate(audit.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
